"use client";

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

const categories = [
{
  id: 'life-support',
  zh: '生命資訊與支援',
  en: 'Life Information & Support',
  desc: '提供完整的生命體徵監測及支援設備，確保患者在各種臨床環境下得到最全面的照護。',
  products: ['監視器 Patient Monitor', '麻醉機 Anaesthesia Machine', '呼吸器 Ventilator', '去顫儀 Defibrillator'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_14dda58c9-1767781819738.png",
  imageAlt: 'Medical monitors and life support equipment in an ICU setting',
  color: '#1E5F8E',
  lightColor: '#EBF3FA',
  span: 'lg:col-span-2'
},
{
  id: 'ivd',
  zh: '體外診斷',
  en: 'In Vitro Diagnostics',
  desc: '涵蓋多種分析儀及配套試劑，提供精確可靠的體外診斷解決方案。',
  products: ['血球分析儀 Haematology Analyser', '生化分析儀 Biochemistry Analyser', '化學發光分析儀 Chemiluminescence', '配套試劑 Reagents'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18a31a924-1772903774172.png",
  imageAlt: 'Laboratory diagnostic equipment and test tubes in a clinical lab',
  color: '#6AAF3D',
  lightColor: '#EEF7E6',
  span: 'lg:col-span-1'
},
{
  id: 'imaging',
  zh: '醫學影像',
  en: 'Medical Imaging',
  desc: '提供先進的超音波影像設備，助力精準診斷。',
  products: ['彩超 Colour Ultrasound', '數位超音波 Digital Ultrasound'],
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d7250a44-1772082480418.png",
  imageAlt: 'Ultrasound imaging equipment being used in a medical examination',
  color: '#2472A4',
  lightColor: '#EBF3FA',
  span: 'lg:col-span-1'
}];


export default function CategoriesSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24" style={{ background: 'var(--color-background)' }}>
      <div className="max-w-8xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div className="space-y-4 reveal-on-scroll">
            <span className="section-label">業務範圍 · Business Scope</span>
            <h2 className="font-display font-800 text-foreground leading-tight" style={{ fontWeight: 800, fontSize: 'clamp(28px, 3.5vw, 48px)' }}>
              三大核心業務領域
              <br />
              <span style={{ color: 'var(--color-primary)' }}>Three Core Divisions</span>
            </h2>
          </div>
          <div className="reveal-on-scroll reveal-delay-1">
            <Link
              href="/products-services"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-display font-700 text-white transition-all duration-300 hover:opacity-90 hover:shadow-glow"
              style={{ background: 'linear-gradient(135deg, #1E5F8E, #2472A4)', fontWeight: 700, fontSize: '14px' }}>
              
              查看全部產品
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Bento grid — asymmetric */}
        <div className="grid lg:grid-cols-3 gap-6">
          {categories?.map((cat, idx) =>
          <div
            key={cat?.id}
            className={`${cat?.span} reveal-on-scroll reveal-delay-${idx + 1} group`}>
            
              <Link href="/products-services">
                <div className="relative rounded-3xl overflow-hidden bg-white border border-border card-hover h-full" style={{ minHeight: idx === 0 ? '420px' : '380px' }}>
                  {/* Image */}
                  <div className="absolute inset-0">
                    <AppImage
                    src={cat?.image}
                    alt={cat?.imageAlt}
                    fill
                    className="object-cover transition-transform duration-700 group-hover:scale-105" />
                  
                    <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${cat?.color}EE 0%, ${cat?.color}88 40%, ${cat?.color}22 100%)` }} />
                  </div>

                  {/* Content */}
                  <div className="relative z-10 h-full flex flex-col justify-end p-7">
                    {/* Label */}
                    <div className="mb-4">
                      <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-700 bg-white/20 text-white backdrop-blur-sm border border-white/20" style={{ fontWeight: 700 }}>
                        <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                        {cat?.en}
                      </span>
                    </div>

                    <h3 className="font-display font-800 text-white mb-2" style={{ fontWeight: 800, fontSize: idx === 0 ? '28px' : '22px' }}>
                      {cat?.zh}
                    </h3>
                    <p className="text-white/80 text-sm leading-relaxed mb-4">{cat?.desc}</p>

                    {/* Product tags */}
                    <div className="flex flex-wrap gap-2">
                      {cat?.products?.slice(0, idx === 0 ? 4 : 2)?.map((p) =>
                    <span key={p} className="px-2.5 py-1 rounded-lg text-xs font-600 bg-white/15 text-white backdrop-blur-sm border border-white/15" style={{ fontWeight: 600 }}>
                          {p?.split(' ')?.[0]}
                        </span>
                    )}
                    </div>

                    {/* Arrow */}
                    <div className="mt-5 flex items-center gap-2 text-white/70 text-sm font-600 group-hover:text-white transition-colors" style={{ fontWeight: 600 }}>
                      了解更多
                      <svg className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>);

}